import { GridViewMetadata, ViewConfiguration, ViewType } from 'apps/webcomponents/src/grid/gridView/types';
import { ColumnDetails, ColumnType } from 'apps/webcomponents/src/grid/types/common';
import { TagsManagerGridWebComponentSchema, Column, ColumnKeys, TagTypeToName } from '../types';

import { Filter } from 'apps/webcomponents/src/grid/gridFilters/types';
import { TagsManagerGridDataProvider } from '../dataProvider/TagsManagerGridDataProvider';
import { renderTagName } from '../../helpers/tagsManagerRenderers';
import moment from 'moment';
import { Dependencies, Permissions } from '../../types/dependencies';
import { DefaultGridColumnFixedWidths, GridColumnConfiguration } from '@nirvana/shared/dtos/types/shared/grid';
import { html } from 'lit';
import { SelectOption } from '../../../ui/gzSelect/types';
import { TagsManagerGridEvents } from '../events/events';

export class GridConfig {
  constructor(public dispatcher: any) {}

  private columnRenderers = {
    [ColumnKeys.TAG_NAME]: renderTagName.bind(null, this.dispatcher.tagNameClickEvent), // rowData => rowData?.item?.name,
    [ColumnKeys.TYPE]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${TagTypeToName[rowData?.item?.type]}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.DESCRIPTION]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.description}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.DATE_CREATED]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${moment(rowData?.item?.created).format('DD MMM YYYY')}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.DATE_MODIFIED]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.lastModified ? moment(rowData?.item?.lastModified).format('DD MMM YYYY') : '-'}"
      ></gz-grid-cell-default-renderer>`;
    },
    [ColumnKeys.COMPANY]: rowData => {
      return html`<gz-grid-cell-default-renderer
        .label="${rowData?.item?.company?.name}"
      ></gz-grid-cell-default-renderer>`;
    },
  };

  private pageSizeOptions = [
    {
      title: '20',
      value: '20',
    },
    {
      title: '50',
      value: '50',
    },
    {
      title: '100',
      value: '100',
    },
  ];

  public getDataForView(dependencies: Dependencies): TagsManagerGridWebComponentSchema {
    const { permissions, translateFn, getCurrentUserCompany } = dependencies;

    const defaultViewProps: ViewConfiguration<Column, ColumnKeys> = {
      id: 'tags-manager-default-view',
      name: '',
      type: ViewType.DEFAULT,
      filters: this.getDefaultFilters(permissions, dependencies.getCurrentUserCompany().id),
      displayedColumns: this.getColumns(),
      compactView: false,
      pageSize: Number(this.pageSizeOptions[0].value),
      columnConfigs: this.getColumnConfigs(),
    };

    const defaultViewProperties: GridViewMetadata<Column, ColumnKeys> = {
      viewId: defaultViewProps.id,
      columns: defaultViewProps.displayedColumns,
      allColumnDetails: this.getColumnDetails(translateFn, permissions, getCurrentUserCompany),
      filters: defaultViewProps.filters,
      predefinedFiltersKeys: this.getPredefinedFiltersKeys(permissions),
      pageSize: defaultViewProps.pageSize,
      pageNumber: 1,
      isCompactViewChecked: defaultViewProps.compactView,
      hasSelectableRows: true,
      hasSelectionExtraOptions: true,
      hasPagination: true,
      pageSizeOptions: this.pageSizeOptions,
      columnRenderers: this.columnRenderers,
      columnConfigs: defaultViewProps.columnConfigs!,
      defaultFlexGrowColumn: ColumnKeys.DATE_MODIFIED,
      dataProvider: new TagsManagerGridDataProvider(dependencies),
      showGlobalActions: true,
      defaultViewProps: defaultViewProps,
      inlineRowActions: this.getInlineRowActions(translateFn),
    };

    return {
      defaultViewProperties,
    };
  }

  private getColumns(): Column[] {
    return [
      Column.TAG_NAME,
      Column.TYPE,
      Column.DESCRIPTION,
      Column.DATE_CREATED,
      Column.DATE_MODIFIED,
      Column.COMPANY,
    ];
  }

  private getColumnDetails(
    translateFn: Function,
    permissions: Permissions,
    getCurrentUserCompany: Function
  ): Array<ColumnDetails<Column, ColumnKeys>> {
    const columnDetails = [
      {
        column: Column.TAG_NAME,
        key: ColumnKeys.TAG_NAME,
        columnName: translateFn('Tag name'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
      },
      {
        column: Column.TYPE,
        key: ColumnKeys.TYPE,
        columnName: translateFn('Type'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
        filteringDisabled: true,
      },
      {
        column: Column.DESCRIPTION,
        key: ColumnKeys.DESCRIPTION,
        columnName: translateFn('Description'),
        type: ColumnType.STRING,
        removable: true,
        sortable: true,
        filteringDisabled: true,
      },
      {
        column: Column.DATE_CREATED,
        key: ColumnKeys.DATE_CREATED,
        columnName: translateFn('Created on'),
        type: ColumnType.DATE,
        removable: true,
        sortable: true,
        filteringDisabled: true,
      },
      {
        column: Column.DATE_MODIFIED,
        key: ColumnKeys.DATE_MODIFIED,
        columnName: translateFn('Last modified'),
        type: ColumnType.DATE,
        removable: true,
        sortable: true,
        filteringDisabled: true,
      },
    ];

    if (permissions.canHaveChildCompanies) {
      const indexToInsert = 5;
      const deleteCount = 0;
      const columnToInsert = {
        column: Column.COMPANY,
        key: ColumnKeys.COMPANY,
        columnName: translateFn('Company'),
        type: ColumnType.COMPANY,
        removable: false,
        sortable: true,
        filterResetValue: [getCurrentUserCompany().id],
      };

      columnDetails.splice(indexToInsert, deleteCount, columnToInsert);
    }

    return columnDetails;
  }

  private getColumnConfigs(): Array<GridColumnConfiguration> {
    return [
      {
        key: ColumnKeys.TAG_NAME,
        width: DefaultGridColumnFixedWidths.M,
      },
      {
        key: ColumnKeys.TYPE,
        width: '110px',
      },
      {
        key: ColumnKeys.DESCRIPTION,
        width: DefaultGridColumnFixedWidths.L,
      },
      {
        key: ColumnKeys.DATE_CREATED,
        width: DefaultGridColumnFixedWidths.S,
      },
      {
        key: ColumnKeys.DATE_MODIFIED,
        width: DefaultGridColumnFixedWidths.S,
      },
      {
        key: ColumnKeys.COMPANY,
        width: DefaultGridColumnFixedWidths.L,
      },
    ];
  }

  private getDefaultFilters(permissions: Permissions, companyId: string): Array<Filter<ColumnKeys>> {
    let defaultFilters: Array<Filter<ColumnKeys>> = [
      {
        [ColumnKeys.TAG_NAME]: { contains: '' },
      },
    ];

    if (permissions.canHaveChildCompanies) {
      defaultFilters = [
        {
          [ColumnKeys.COMPANY]: {
            in: [companyId],
          },
        },
        ...defaultFilters,
      ];
    }

    return defaultFilters;
  }

  private getPredefinedFiltersKeys(permissions: Permissions): Array<ColumnKeys> {
    let keys = [ColumnKeys.TAG_NAME];

    if (permissions.canHaveChildCompanies) {
      keys = [ColumnKeys.COMPANY, ...keys];
    }

    return keys;
  }

  private getInlineRowActions(translateFn: Function): Array<SelectOption> {
    return [
      {
        title: translateFn('Copy'),
        value: TagsManagerGridEvents.OnCloneTag,
      },
      {
        title: translateFn('Delete'),
        value: TagsManagerGridEvents.OnDeleteTagFromInlineButton,
        customClass: 'danger',
      },
    ];
  }
}
